import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, CheckIcon } from 'lucide-react';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Field, Input, Select, Textarea } from '../ui/Field';
import { customer, allergySummary } from '../../data/customer';
import { equipmentLabel } from '../../data/taxonomy';
import { GROCERY_LABEL, SERVICE_FEE_RATE, missingEquipment, priceQuote } from '../../utils/pricing';
import type { QuoteDraft } from '../../utils/pricing';
import { currency } from '../../utils/format';
import { useLocale } from '../../contexts/LocaleContext';
import type { Chef, Fulfillment } from '../../types/chef';
export function QuoteBuilder({
  chef,
  open,
  onClose,
  onSend,
  seed
}: {
  chef: Chef;
  open: boolean;
  onClose: () => void;
  onSend: (draft: QuoteDraft) => void;
  seed?: {
    guests?: number;
    date?: string;
    time?: string;
    menuId?: string;
    fulfillment?: Fulfillment;
  };
}) {
  const {
    locale,
    currencyCode
  } = useLocale();
  const [menuId, setMenuId] = useState(seed?.menuId ?? chef.menus[0].id);
  const [guests, setGuests] = useState(seed?.guests ?? 6);
  const [date, setDate] = useState(seed?.date ?? '2026-09-19');
  const [time, setTime] = useState(seed?.time ?? '19:00');
  const [addOnIds, setAddOnIds] = useState<string[]>([]);
  const [travelKm, setTravelKm] = useState(9);
  const [note, setNote] = useState('');
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const menu = chef.menus.find(m => m.id === menuId) ?? chef.menus[0];
  const fulfillment: Fulfillment = menu.fulfillment.includes('in_home') ? 'in_home' : 'delivery';
  const [hours, setHours] = useState(menu.includedHours);
  const draft: QuoteDraft = {
    menuId: menu.id,
    fulfillment,
    guests: Math.max(guests, menu.minGuests),
    date,
    time,
    hours,
    addOns: addOnIds.map(id => ({
      addOnId: id
    })),
    travelKm,
    note,
    allergiesAcknowledged: ack
  };
  const priced = useMemo(() => priceQuote(chef, draft), [chef, menuId, guests, hours, addOnIds, travelKm, fulfillment]);
  const gaps = fulfillment === 'in_home' ? missingEquipment(menu, customer.kitchen.equipment) : [];
  const takeHome = priced.subtotal - Math.round(priced.subtotal * SERVICE_FEE_RATE);
  const toggleAddOn = (id: string) => setAddOnIds(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  const submit = () => {
    if (!ack) {
      setError('Acknowledge the dietary record before sending — it prints on the quote.');
      return;
    }
    if (priced.minimumShortfall > 0) {
      setError(`This is ${currency(priced.minimumShortfall, locale, currencyCode)} below your own minimum of ${currency(chef.minimumSpend, locale, currencyCode)}.`);
      return;
    }
    setError(null);
    onSend(draft);
    onClose();
  };
  return <Sheet open={open} onClose={onClose} title="Build a quote" description="Every line is shown to the guest" side="right" data-id="element-157" data-mp="3rx9qeczudv46">
      <div className="space-y-5" data-id="element-158" data-mp="27epit18ya5yw">
        <Field label="Menu" id="qb-menu" data-id="element-159" data-mp="2bhvwuv2b11fx">
          <Select id="qb-menu" value={menuId} onChange={e => setMenuId(e.target.value)} data-id="element-160" data-mp="2bixa966sm00t">
            {chef.menus.map(m => <option key={m.id} value={m.id} data-id="element-161" data-mp="2l86z8d6cb2kp">
                {m.name} · {currency(m.pricePerGuest, locale, currencyCode)}/guest
              </option>)}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3" data-id="element-162" data-mp="2v6d0qto8i6p1">
          <Field label="Guests" id="qb-guests" hint={`min ${menu.minGuests}`} data-id="element-163" data-mp="2v51w6rmysuwv">
            <Input id="qb-guests" type="number" min={menu.minGuests} value={guests} onChange={e => setGuests(Number(e.target.value) || menu.minGuests)} className="tnum" data-id="element-164" data-mp="2z7q8j9aw0jyl" />
          </Field>
          <Field label="Hours on site" id="qb-hours" hint={`${menu.includedHours} included`} data-id="element-165" data-mp="19sibhjl9hziu">
            <Input id="qb-hours" type="number" min={0} step={0.5} value={hours} onChange={e => setHours(Number(e.target.value) || 0)} className="tnum" disabled={fulfillment === 'delivery'} data-id="element-166" data-mp="eiljr38i2cmn" />
          </Field>
          <Field label="Date" id="qb-date" data-id="element-167" data-mp="jj9kwhqg09ul">
            <Input id="qb-date" type="date" value={date} onChange={e => setDate(e.target.value)} data-id="element-168" data-mp="jg3r4p6vcjsj" />
          </Field>
          <Field label="Start" id="qb-time" data-id="element-169" data-mp="3myx5333k7an8">
            <Input id="qb-time" type="time" value={time} onChange={e => setTime(e.target.value)} data-id="element-170" data-mp="3n089n54twmfe" />
          </Field>
        </div>

        <div data-id="element-171" data-mp="5k06nnlsrpt9">
          <p className="mb-2 text-[13px] font-semibold text-ink" data-id="element-172" data-mp="rqiod48sztm">Add-ons</p>
          <ul className="space-y-1.5" data-id="element-173" data-mp="pm4918pwif3">
            {chef.addOns.filter(a => a.unit !== 'per_km').map(addOn => {
            const on = addOnIds.includes(addOn.id);
            return <li key={addOn.id} data-id="element-174" data-mp="1jpmpxpc5jair">
                    <label className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors duration-150 ease-out ${on ? 'border-primary bg-primary-soft' : 'border-hairline hover:border-primary/40'}`} data-id="element-175" data-mp="1jok3btlv3u8n">
                      <input type="checkbox" checked={on} onChange={() => toggleAddOn(addOn.id)} className="accent-primary" data-id="element-176" data-mp="1eop8xg2vomiu" />
                      <span className="min-w-0 flex-1 text-sm text-ink" data-id="element-177" data-mp="27g8z5jxw70uc">{addOn.name}</span>
                      <span className="tnum shrink-0 text-[13px] text-ink-2" data-id="element-178" data-mp="1tg7xw3dptrec">
                        {currency(addOn.price, locale, currencyCode)}
                      </span>
                    </label>
                  </li>;
          })}
          </ul>
        </div>

        <Field label="Distance to the guest" id="qb-travel" hint={fulfillment === 'delivery' ? `free within ${chef.deliveryFreeRadiusKm} km` : `${chef.travelIncludedKm} km included`} data-id="element-179" data-mp="i35ez1o58pr0">
          <Input id="qb-travel" type="number" min={0} value={travelKm} onChange={e => setTravelKm(Number(e.target.value) || 0)} className="tnum" data-id="element-180" data-mp="2jpf4tise80ss" />
        </Field>

        {!!gaps.length && <div className="flex items-start gap-2 rounded-lg border border-accent/50 bg-accent-soft p-3" data-id="element-181" data-mp="25yk26o8hmfxn">
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-on-accent" aria-hidden="true" data-id="element-182" data-mp="20zgzvbjw6ntd" />
            <p className="text-[12px] leading-snug text-on-accent" data-id="element-183" data-mp="1w04aaj1mz2n3">
              <span className="font-semibold" data-id="element-184" data-mp="1w2gqaqnu32xi">Kitchen check · </span>
              this menu needs {gaps.map(id => equipmentLabel(id).toLowerCase()).join(', ')}. Their kitchen lists none of it —
              plan to bring your own or say so in the note.
            </p>
          </div>}

        <Field label="Note to the guest" id="qb-note" data-id="element-185" data-mp="3ld7s4unwx1n9">
          <Textarea id="qb-note" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Separate wok and board for the shellfish allergy. Mild version of every dish for Ing." data-id="element-186" data-mp="3tx4a7dufglu7" />
        </Field>

        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-hairline bg-oat p-3" data-id="element-187" data-mp="biv2kw12k13n">
          <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} className="mt-0.5 accent-primary" data-id="element-188" data-mp="3ay8luj5931oa" />
          <span className="text-[13px] text-ink-2" data-id="element-189" data-mp="3ezkbgoo4jmwh">
            <span className="font-semibold text-ink" data-id="element-190" data-mp="3jyewlx9kdnjr">I have read the dietary record. </span>
            {allergySummary()}
          </span>
        </label>

        <div className="rounded-xl border border-hairline p-4" data-id="element-191" data-mp="3oyjdhoedwkai">
          <dl className="space-y-1.5 text-[13px]" data-id="element-192" data-mp="z6utbvydrri8">
            {priced.lines.map(line => <div key={line.id} className="flex justify-between gap-3" data-id="element-193" data-mp="1467r3bqsiizl">
                <dt className="text-ink-2" data-id="element-194" data-mp="1pas0xb33lask">{line.label}</dt>
                <dd className="tnum text-ink" data-id="element-195" data-mp="1pbb9i16uymtx">
                  {line.amount === 0 ? 'at cost' : currency(line.amount, locale, currencyCode)}
                </dd>
              </div>)}
            <div className="flex justify-between gap-3 border-t border-hairline pt-2" data-id="element-196" data-mp="1z25vr5k3tmfd">
              <dt className="text-ink-2" data-id="element-197" data-mp="1u8l6c1wku0rs">Service fee &amp; VAT</dt>
              <dd className="tnum text-ink" data-id="element-198" data-mp="28yiwuohzb2k5">{currency(priced.serviceFee + priced.tax, locale, currencyCode)}</dd>
            </div>
            <div className="flex justify-between gap-3" data-id="element-199" data-mp="23z64jo8c93a6">
              <dt className="font-display text-base font-semibold text-ink" data-id="element-200" data-mp="2hzgwyvpaqpxg">Guest pays</dt>
              <dd className="tnum font-display text-base font-semibold text-ink" data-id="element-201" data-mp="2d0vsgk30hk37">
                {currency(priced.total, locale, currencyCode)}
              </dd>
            </div>
            <div className="flex justify-between gap-3 rounded-lg bg-primary-soft px-3 py-2" data-id="element-202" data-mp="nnivjfx8ayc4">
              <dt className="font-medium text-primary" data-id="element-203" data-mp="iope5cetmn6o">Your take-home</dt>
              <dd className="tnum font-semibold text-primary" data-id="element-204" data-mp="2uahxp1os8ydp">{currency(takeHome, locale, currencyCode)}</dd>
            </div>
          </dl>
          <p className="mt-2 text-[12px] text-ink-3" data-id="element-205" data-mp="2lhmmlrcmw8g0">{GROCERY_LABEL[menu.groceries]} · deposit {chef.depositPercent}%</p>
        </div>

        {error && <p role="alert" className="text-sm font-medium text-danger" data-id="element-206" data-mp="2phor089r7fyz">
            {error}
          </p>}

        <div className="flex gap-2" data-id="element-207" data-mp="2bi5n5rnz6qsh">
          <Button variant="action" full onClick={submit} data-id="element-208" data-mp="2gia57m4j7tkt">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-209" data-mp="2gjuyl8f416h3" />
            Send quote
          </Button>
          <Button variant="ghost" onClick={onClose} data-id="element-210" data-mp="22kv9y0rlkrkx">
            Cancel
          </Button>
        </div>
      </div>
    </Sheet>;
}